import { useState } from 'react';
import { Star, Users, Trophy, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Profile } from '@/types/game';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CreatorStudioProps {
  profile: Profile;
}

export function CreatorStudio({ profile }: CreatorStudioProps) {
  const [showCreateLeague, setShowCreateLeague] = useState(false);
  const [leagueName, setLeagueName] = useState('');
  const [leagueDescription, setLeagueDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateLeague = async () => {
    if (!leagueName.trim()) {
      toast.error('Please enter a league name');
      return;
    }

    setIsCreating(true);
    try {
      const joinCode = !isPublic ? Math.random().toString(36).substring(2, 8).toUpperCase() : null;
      
      const { data: league, error } = await supabase
        .from('leagues')
        .insert({
          name: leagueName.trim(),
          description: leagueDescription.trim() || null,
          owner_user_id: profile.id,
          is_public: isPublic,
          join_code: joinCode,
        })
        .select()
        .single();

      if (error) throw error;

      // Add creator as owner member
      await supabase.from('league_members').insert({
        league_id: league.id,
        user_id: profile.id,
        role: 'owner',
      });

      toast.success('League created successfully!');
      setShowCreateLeague(false);
      setLeagueName('');
      setLeagueDescription('');
    } catch (err) {
      console.error('Error creating league:', err);
      toast.error('Failed to create league');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="game-card">
        <div className="flex items-center gap-3 mb-4">
          <Star className="w-6 h-6 text-accent fill-accent" />
          <h2 className="text-xl font-bold">Creator Studio</h2>
        </div>
        <p className="text-muted-foreground mb-6">
          As a verified creator, you have access to special features to engage your community.
        </p>

        {/* Create League */}
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" />
            Create a League
          </h3>
          
          {!showCreateLeague ? (
            <Button onClick={() => setShowCreateLeague(true)} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Create New League
            </Button>
          ) : (
            <div className="space-y-4 p-4 bg-background/50 rounded-lg">
              <div>
                <Label htmlFor="league-name">League Name</Label>
                <Input
                  id="league-name"
                  value={leagueName}
                  onChange={(e) => setLeagueName(e.target.value)}
                  placeholder="My Awesome League"
                />
              </div>
              <div>
                <Label htmlFor="league-desc">Description (optional)</Label>
                <Textarea
                  id="league-desc"
                  value={leagueDescription}
                  onChange={(e) => setLeagueDescription(e.target.value)}
                  placeholder="What's this league about?"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                <Label>Public league (anyone can join)</Label>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateLeague} disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create League'}
                </Button>
                <Button variant="ghost" onClick={() => setShowCreateLeague(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Featured Match Placeholder */}
        <div className="mt-8 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            Featured Match
          </h3>
          <p className="text-sm text-muted-foreground">
            Pin a featured match to your profile for your followers to play.
          </p>
          <Button variant="outline" disabled>
            Select Featured Match (Coming Soon)
          </Button>
        </div>
      </div>
    </div>
  );
}